import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Search, FileText, Users, Zap, Building2, AlertCircle } from 'lucide-react'
import { searchService, SearchResultItem } from '@/services/search.service'
import { useToast } from '@/components/ui/use-toast'

interface SearchResult {
  id: string
  type: 'device' | 'account' | 'user' | 'alert' | 'document'
  title: string
  description: string
  url: string
  metadata?: Record<string, string>
}

const getIcon = (type: SearchResult['type']) => {
  switch (type) {
    case 'device':
      return <Zap className="h-4 w-4" />
    case 'account':
      return <Building2 className="h-4 w-4" />
    case 'user':
      return <Users className="h-4 w-4" />
    case 'alert':
      return <AlertCircle className="h-4 w-4" />
    case 'document':
      return <FileText className="h-4 w-4" />
  }
}

const getTypeColor = (type: SearchResult['type']) => {
  switch (type) {
    case 'device':
      return 'bg-blue-100 text-blue-800 border-blue-300'
    case 'account':
      return 'bg-green-100 text-green-800 border-green-300'
    case 'user':
      return 'bg-purple-100 text-purple-800 border-purple-300'
    case 'alert':
      return 'bg-orange-100 text-orange-800 border-orange-300'
    case 'document':
      return 'bg-gray-100 text-gray-800 border-gray-300'
  }
}

export const GlobalSearchPage = () => {
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = async (term: string) => {
    setSearchTerm(term)

    if (term.length < 2) {
      setResults([])
      return
    }

    setIsSearching(true)
    try {
      const response = await searchService.search(term, 50)
      setResults(response.results)
    } catch (error: any) {
      console.error('Search failed:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to search. Please try again.',
        variant: 'destructive',
      })
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const filterByType = (type?: SearchResult['type']) => {
    if (!type) return results
    return results.filter(r => r.type === type)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Global Search</h1>
        <p className="text-muted-foreground">
          Search across devices, accounts, users, and more
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for devices, accounts, users, alerts..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 text-lg h-12"
            />
          </div>
        </CardContent>
      </Card>

      {searchTerm.length >= 2 && (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">
              All Results ({results.length})
            </TabsTrigger>
            <TabsTrigger value="devices">
              Devices ({filterByType('device').length})
            </TabsTrigger>
            <TabsTrigger value="accounts">
              Accounts ({filterByType('account').length})
            </TabsTrigger>
            <TabsTrigger value="users">
              Users ({filterByType('user').length})
            </TabsTrigger>
            <TabsTrigger value="alerts">
              Alerts ({filterByType('alert').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {isSearching ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Searching...
                </CardContent>
              </Card>
            ) : results.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No results found for "{searchTerm}"
                </CardContent>
              </Card>
            ) : (
              results.map((result) => (
                <Link key={result.id} to={result.url}>
                  <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-primary/10 p-2">
                          {getIcon(result.type)}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {result.title}
                            <Badge className={getTypeColor(result.type)}>
                              {result.type}
                            </Badge>
                          </CardTitle>
                          <CardDescription>{result.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    {result.metadata && (
                      <CardContent>
                        <div className="flex gap-4 text-sm text-muted-foreground">
                          {Object.entries(result.metadata).map(([key, value]) => (
                            <span key={key}>
                              <span className="capitalize">{key}: </span>
                              <span className="font-medium">{value}</span>
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                </Link>
              ))
            )}
          </TabsContent>

          {['devices', 'accounts', 'users', 'alerts'].map((type) => (
            <TabsContent key={type} value={type} className="space-y-4">
              {filterByType(type as SearchResult['type']).map((result) => (
                <Link key={result.id} to={result.url}>
                  <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-primary/10 p-2">
                          {getIcon(result.type)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{result.title}</CardTitle>
                          <CardDescription>{result.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  )
}
